<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { LocalePicker, type LocaleCode, type LocaleOption } from '@mybonet/i18n'
import { extractCssVarValue } from './seo'
import themeRwdKitCss from '../../../src/MyboNet.Application/Assets/theme-rwd-kit.css?raw'

export type ThemePackage = {
  cdnBase: string
  prefix: string
  body: string
  css: string[]
  js: string[]
  fonts?: string[]
  images?: Record<string, string>
}

export type PackageBootstrap = {
  siteKey: string
  displayName: string
  themeKey: string
  theme: {
    themeKey: string
    name: string
    package?: ThemePackage | null
    tokens?: Record<string, string>
  }
  siteAssetOverrides?: Record<string, string> | null
}

type Product = {
  id: string
  sku: string
  name: string
  description?: string
  price: number
  currency: string
  stockQty?: number
  status?: string
  coverImageUrl?: string | null
  coverObjectKey?: string | null
}

type PublicAlbum = {
  id: string
  title: string
  description?: string | null
  sortOrder: number
  assets: { id: string; objectKey: string; title?: string | null; sortOrder: number; url: string }[]
}

type PublicVideo = {
  id: string
  slug: string
  title: string
  bodyHtml?: string | null
}

type PublicNews = {
  id: string
  slug: string
  title: string
  bodyHtml?: string | null
}

type PublicAnnounce = {
  id: string
  slug: string
  title: string
  bodyHtml?: string | null
}

type NavLink = {
  id: string
  label: string
  el: HTMLElement
}

const props = defineProps<{
  bootstrap: PackageBootstrap
  apiBase: string
  products: Product[]
  albums?: PublicAlbum[]
  videos?: PublicVideo[]
  news?: PublicNews[]
  announces?: PublicAnnounce[]
  contact?: { address?: string; telephone?: string; email?: string }
  social?: { network: string; href: string }[]
  features?: { title: string; description?: string }[]
  ctas?: { label: string; role: string; href: string }[]
  team?: { role: string; bio?: string }[]
  specs?: { label: string; value: string }[]
  locale: LocaleCode
  localeOptions: LocaleOption[]
  localeLabel?: string
}>()

const emit = defineEmits<{
  localeChange: [code: LocaleCode]
}>()

const host = ref<HTMLElement | null>(null)
const loadError = ref('')
const injected: HTMLElement[] = []
const menuOpen = ref(false)
const navLinks = ref<NavLink[]>([])
const scanTimers: number[] = []
const mountedBase = ref('')
const mountedOverrides = ref<Record<string, string> | null>(null)

function pkgBase(pkg: ThemePackage) {
  return `${pkg.cdnBase.replace(/\/$/, '')}/${pkg.prefix.replace(/^\/|\/$/g, '')}`
}

function resolveAsset(rel: string, pkg: ThemePackage) {
  const overrides = props.bootstrap.siteAssetOverrides
  if (overrides && overrides[rel]) return overrides[rel]
  const mapped = pkg.images?.[rel] || pkg.images?.[rel.split('/').pop() || '']
  const path = mapped || rel
  return `${pkgBase(pkg)}/${String(path).replace(/^\//, '')}`
}

function injectLink(href: string, rel = 'stylesheet') {
  const el = document.createElement('link')
  el.rel = rel
  el.href = href
  el.dataset.myboTheme = '1'
  document.head.appendChild(el)
  injected.push(el)
}

function injectInlineStyle(id: string, css: string) {
  const el = document.createElement('style')
  el.id = id
  el.textContent = css
  el.dataset.myboTheme = '1'
  document.head.appendChild(el)
  injected.push(el)
}

function plainI18nText(raw: string): string | null {
  const stripped = raw
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<svg[\s\S]*?<\/svg>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!stripped) return null
  if (/[<>]/.test(stripped)) return null
  if (/\b(?:stroke|viewBox|fill|clip-rule)\s*=/.test(stripped)) return null
  return stripped
}

/** Apply package langs/{locale}.json onto [data-i18n] / [data-i18n-placeholder] / [data-i18n-attr]. */
async function applyThemeLang(
  root: HTMLElement,
  base: string,
  locale: string,
  overrides?: Record<string, string> | null,
) {
  const load = async (code: string): Promise<Record<string, string> | null> => {
    const rel = `langs/${code}.json`
    const url = overrides?.[rel] || `${base}/${rel}`
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) return null
      const data = (await res.json()) as Record<string, string>
      return data && typeof data === 'object' ? data : null
    } catch {
      return null
    }
  }
  let strings = await load(locale)
  if (!strings && locale !== 'zh-TW') strings = await load('zh-TW')
  if (!strings) return
  ;(window as unknown as { __MYBO__: Record<string, unknown> }).__MYBO__ = {
    ...((window as unknown as { __MYBO__?: Record<string, unknown> }).__MYBO__ || {}),
    strings,
    locale,
  }
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n')
    if (!key || strings![key] == null) return
    if (el.closest('svg')) return
    const val = plainI18nText(String(strings![key]))
    if (!val) return
    // Mixed nodes (label + SVG icon): replace the visible text node only.
    const textNodes = [...el.childNodes].filter(
      (node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim(),
    )
    if (el.childElementCount > 0) {
      if (textNodes.length === 1) textNodes[0].textContent = ` ${val} `
      return
    }
    el.textContent = val
  })
  root.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder')
    if (!key || strings![key] == null) return
    const val = plainI18nText(String(strings![key]))
    if (!val) return
    el.setAttribute('placeholder', val)
  })
  root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    const spec = el.getAttribute('data-i18n-attr')
    if (!spec) return
    for (const part of spec.split(',')) {
      const [attr, key] = part.split(':').map((s) => s.trim())
      if (!attr || !key || strings![key] == null) continue
      const val = plainI18nText(String(strings![key]))
      if (!val) continue
      el.setAttribute(attr, val)
    }
  })
}

/** Load Google Fonts named in prompt tokens.override.css (--font-display / --font-body). */
async function injectFontsFromTokenOverride(url: string) {
  try {
    const res = await fetch(url, { cache: 'force-cache' })
    if (!res.ok) return
    const css = await res.text()
    const names = ['--font-display', '--font-body', '--font-cjk']
      .map((k) => extractCssVarValue(css, k))
      .filter((n): n is string => !!n && /^[A-Za-z][A-Za-z0-9 \-]+$/.test(n))
    const alias: Record<string, string> = {
      'Source Sans Pro': 'Source Sans 3',
      'Fredoka One': 'Fredoka',
    }
    const unique = [...new Set(names.map((n) => alias[n.trim()] || n.trim()))]
    if (unique.length === 0) return
    const q = unique
      .map((n) => `family=${encodeURIComponent(n).replace(/%20/g, '+')}:wght@300;400;600;700;900`)
      .join('&')
    injectLink(`https://fonts.googleapis.com/css2?${q}&display=swap`)
  } catch {
    /* non-fatal */
  }
}

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = src
    el.async = false
    el.dataset.myboTheme = '1'
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('script_load_failed:' + src))
    document.body.appendChild(el)
    injected.push(el)
  })
}

function clearScanTimers() {
  while (scanTimers.length) {
    const id = scanTimers.pop()
    if (id != null) window.clearTimeout(id)
  }
}

function closeMenu() {
  menuOpen.value = false
  document.body.classList.remove('mybo-pkg-menu-open')
}

function openMenu() {
  scanNavLinks()
  menuOpen.value = true
  document.body.classList.add('mybo-pkg-menu-open')
}

function toggleMenu() {
  if (menuOpen.value) closeMenu()
  else openMenu()
}

function onLocaleChange(code: LocaleCode) {
  emit('localeChange', code)
  closeMenu()
}

function activateNav(link: NavLink) {
  closeMenu()
  try {
    link.el.click()
  } catch {
    /* ignore */
  }
}

function scanNavLinks() {
  const root = host.value
  if (!root) {
    navLinks.value = []
    return
  }
  const selectors = [
    'header a[href]',
    'nav a[href]',
    '[role="navigation"] a[href]',
    '.navbar a[href]',
    '.nav a[href]',
    '.site-nav a[href]',
    '.main-nav a[href]',
    '.top-nav a[href]',
    '.nav-links a[href]',
    '.menu a[href]',
    '.header-nav a[href]',
    '.nav-item a',
    'a.nav-link',
    'a[onclick*="navigate"]',
    'button[onclick*="navigate"]',
    '[data-nav] a',
    '.header-main a',
  ]
  const seen = new Set<string>()
  const found: NavLink[] = []
  for (const sel of selectors) {
    root.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      const label = (el.textContent || el.getAttribute('title') || el.getAttribute('aria-label') || '')
        .replace(/\s+/g, ' ')
        .trim()
      if (!label || label.length > 48) return
      if (/^(☰|✕|×|cart|🛒)$/i.test(label)) return
      const href = (el.getAttribute('href') || el.getAttribute('onclick') || '').trim()
      const key = `${label}|${href}`
      if (seen.has(key)) return
      seen.add(key)
      found.push({ id: key, label, el })
    })
    if (found.length >= 18) break
  }
  navLinks.value = found.slice(0, 18)
}

function scheduleNavScans() {
  clearScanTimers()
  void nextTick(() => scanNavLinks())
  for (const ms of [120, 400, 1200]) {
    scanTimers.push(window.setTimeout(() => scanNavLinks(), ms))
  }
}

function cleanup() {
  clearScanTimers()
  closeMenu()
  injected.splice(0).forEach((el) => el.remove())
  if (host.value) host.value.innerHTML = ''
  navLinks.value = []
  delete (window as unknown as { __MYBO__?: unknown }).__MYBO__
  document.documentElement.classList.remove('mybo-theme-package')
  mountedBase.value = ''
  mountedOverrides.value = null
}

async function mountPackage() {
  cleanup()
  loadError.value = ''
  const pkg = props.bootstrap.theme.package
  if (!pkg || !host.value) return

  document.documentElement.classList.add('mybo-theme-package')
  document.documentElement.lang = props.locale || 'zh-Hant'

  const base = pkgBase(pkg)
  const overrides = props.bootstrap.siteAssetOverrides
  ;(window as unknown as { __MYBO__: Record<string, unknown> }).__MYBO__ = {
    siteKey: props.bootstrap.siteKey,
    displayName: props.bootstrap.displayName,
    apiBase: props.apiBase,
    cdnBase: pkg.cdnBase,
    themeKey: props.bootstrap.themeKey,
    locale: props.locale,
    products: props.products,
    albums: props.albums || [],
    videos: props.videos || [],
    news: props.news || [],
    announces: props.announces || [],
    // Alias for theme packages that look for case studies.
    cases: props.announces || [],
    contact: props.contact || {},
    social: props.social || [],
    features: props.features || [],
    ctas: props.ctas || [],
    team: props.team || [],
    specs: props.specs || [],
    assetUrl: (rel: string) => resolveAsset(rel, pkg),
  }

  for (const font of pkg.fonts || []) {
    if (!font) continue
    if (font.includes('fonts.googleapis.com/css')) injectLink(font, 'stylesheet')
    else {
      const el = document.createElement('link')
      el.rel = 'preconnect'
      el.href = font
      el.dataset.myboTheme = '1'
      document.head.appendChild(el)
      injected.push(el)
    }
  }
  for (const css of pkg.css || []) {
    const rel = css.replace(/^\//, '')
    const href = overrides?.[rel] || `${base}/${rel}`
    injectLink(href)
  }
  const tokenOverride = overrides?.['tokens.override.css']
  if (tokenOverride) {
    await injectFontsFromTokenOverride(tokenOverride)
    injectLink(tokenOverride)
  }
  injectInlineStyle('mybo-theme-rwd-kit', themeRwdKitCss)
  const rwdKit = overrides?.['rwd.kit.css']
  if (rwdKit) injectLink(rwdKit)

  async function fetchBodyHtml(url: string): Promise<string | null> {
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) return null
      return await res.text()
    } catch {
      return null
    }
  }

  // Site override → CDN package → same-origin API. Non–zh-TW locales need CDN body (data-i18n).
  const bodyRel = (pkg.body || 'body.html').replace(/^\//, '')
  const localBody = `${props.apiBase.replace(/\/$/, '')}/api/v2/theme-packages/${encodeURIComponent(props.bootstrap.themeKey)}/body`
  const cdnBody = overrides?.[bodyRel] || `${base}/${bodyRel}`
  const loc = props.locale || 'zh-TW'
  const preferCdn = loc !== 'zh-TW'
  const ordered = preferCdn
    ? [overrides?.[bodyRel], cdnBody, localBody]
    : [overrides?.[bodyRel], localBody, cdnBody]

  let html = ''
  for (const bodyUrl of ordered.filter(Boolean) as string[]) {
    const candidate = await fetchBodyHtml(bodyUrl)
    if (!candidate) continue
    html = candidate
    break
  }
  // EC2 theme copy may predate data-i18n — fall back to CDN when local lacks markers.
  if (!html.includes('data-i18n') && cdnBody) {
    const cdn = await fetchBodyHtml(cdnBody)
    if (cdn?.includes('data-i18n')) html = cdn
  }
  if (!html) throw new Error('Failed to fetch theme body')

  // Apply site overrides for image/media paths already pointing at CDN theme assets
  if (overrides) {
    for (const [rel, url] of Object.entries(overrides)) {
      const file = rel.split('/').pop() || rel
      html = html.replaceAll(`${base}/${rel}`, url)
      html = html.replaceAll(`${base}/images/${file}`, url)
      html = html.replaceAll(`${base}/media/${file}`, url)
      html = html.replaceAll(`images/${file}`, url)
      html = html.replaceAll(`media/${file}`, url)
    }
  }

  host.value.innerHTML = html
  mountedBase.value = base
  mountedOverrides.value = overrides ?? null
  await applyThemeLang(host.value, base, props.locale || 'zh-TW', overrides)

  for (const js of pkg.js || []) {
    const rel = js.replace(/^\//, '')
    const src = overrides?.[rel] || `${base}/${rel}`
    await injectScript(`${src}${src.includes('?') ? '&' : '?'}t=${Date.now()}`)
  }

  scheduleNavScans()
}

onMounted(() => {
  void mountPackage().catch((e) => {
    loadError.value = e instanceof Error ? e.message : 'theme_package_failed'
  })
})

watch(
  () => props.locale,
  async (loc) => {
    if (!host.value || !mountedBase.value) return
    document.documentElement.lang = loc || 'zh-Hant'
    // The mounted body already falls back to the CDN copy when the local copy
    // lacks data-i18n markers. Re-running a classic theme script here would
    // redeclare top-level const/let bindings (for example TP or PETS).
    await applyThemeLang(host.value, mountedBase.value, loc || 'zh-TW', mountedOverrides.value)
  },
)

watch(
  () => [
    props.products,
    props.albums,
    props.videos,
    props.news,
    props.announces,
    props.contact,
    props.social,
    props.features,
    props.ctas,
    props.team,
    props.specs,
  ] as const,
  () => {
    const runtime = (window as unknown as { __MYBO__?: Record<string, unknown> }).__MYBO__
    if (!runtime) return
    Object.assign(runtime, {
      products: props.products,
      albums: props.albums || [],
      videos: props.videos || [],
      news: props.news || [],
      announces: props.announces || [],
      cases: props.announces || [],
      contact: props.contact || {},
      social: props.social || [],
      features: props.features || [],
      ctas: props.ctas || [],
      team: props.team || [],
      specs: props.specs || [],
    })
    window.dispatchEvent(new CustomEvent('mybo:data', { detail: runtime }))
  },
  { deep: true },
)

onBeforeUnmount(cleanup)
</script>

<template>
  <div class="theme-package-root">
    <p v-if="loadError" class="theme-package-error">{{ loadError }}</p>

    <div class="mybo-pkg-chrome" data-mybo-chrome="1">
      <button
        type="button"
        class="mybo-pkg-menu-btn"
        :aria-expanded="menuOpen"
        aria-controls="mybo-pkg-drawer"
        aria-label="Menu"
        @click="toggleMenu"
      >
        <span aria-hidden="true">{{ menuOpen ? '✕' : '☰' }}</span>
      </button>
      <LocalePicker
        class="mybo-pkg-locale"
        :locale="locale"
        :options="localeOptions"
        :label="localeLabel || 'Language'"
        @change="onLocaleChange"
      />
    </div>

    <button
      type="button"
      class="mybo-pkg-drawer-backdrop"
      :class="{ open: menuOpen }"
      aria-label="Close menu"
      @click="closeMenu"
    />
    <aside id="mybo-pkg-drawer" class="mybo-pkg-drawer" :class="{ open: menuOpen }" :aria-hidden="!menuOpen">
      <div class="mybo-pkg-drawer-title">{{ bootstrap.displayName }}</div>
      <ul class="mybo-pkg-drawer-menu">
        <li v-for="link in navLinks" :key="link.id">
          <button type="button" @click="activateNav(link)">{{ link.label }}</button>
        </li>
        <li v-if="!navLinks.length" class="mybo-pkg-drawer-empty">Menu</li>
      </ul>
      <LocalePicker
        class="mybo-pkg-drawer-locale"
        variant="list"
        :locale="locale"
        :options="localeOptions"
        :label="localeLabel || 'Language'"
        @change="onLocaleChange"
      />
    </aside>

    <div ref="host" class="theme-package-host" />
  </div>
</template>

<style>
.theme-package-root {
  min-height: 100vh;
  position: relative;
}
.theme-package-error {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10060;
  background: #c0392b;
  color: #fff;
  padding: 0.75rem 1rem;
  margin: 0;
  font: 14px/1.4 system-ui, sans-serif;
}
.theme-package-host {
  /* design CSS owns layout; neutralize shell defaults */
  isolation: isolate;
}

/* Global chrome for every CDN theme package (locale + mobile menu). */
.mybo-pkg-chrome {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 10040;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 12px;
  background: rgba(12, 14, 18, 0.72);
  color: #f4f6f8;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  overflow: visible;
  isolation: auto;
}
.mybo-pkg-menu-btn {
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.mybo-pkg-menu-btn:hover,
.mybo-pkg-menu-btn:focus-visible {
  background: rgba(255, 255, 255, 0.12);
  outline: none;
}
.mybo-pkg-locale {
  color: #f4f6f8;
  overflow: visible;
  position: relative;
}
.mybo-pkg-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10045;
  border: 0;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
}
.mybo-pkg-drawer-backdrop.open {
  opacity: 1;
  pointer-events: auto;
}
.mybo-pkg-drawer {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10050;
  width: min(86vw, 320px);
  height: 100%;
  padding: 72px 16px 24px;
  box-sizing: border-box;
  background: #10141c;
  color: #f2f4f7;
  transform: translateX(104%);
  transition: transform 0.2s ease;
  overflow: auto;
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.35);
}
.mybo-pkg-drawer.open {
  transform: translateX(0);
}
.mybo-pkg-drawer-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.7;
  margin: 0 0 14px;
}
.mybo-pkg-drawer-menu {
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.mybo-pkg-drawer-menu button {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: inherit;
  border-radius: 10px;
  padding: 12px 14px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.mybo-pkg-drawer-menu button:hover,
.mybo-pkg-drawer-menu button:focus-visible {
  border-color: #ff3333;
  outline: none;
}
.mybo-pkg-drawer-empty {
  opacity: 0.55;
  font-size: 14px;
  padding: 8px 4px;
}
.mybo-pkg-drawer-locale {
  margin-top: 8px;
  color: #f2f4f7;
}

@media (max-width: 900px) {
  .mybo-pkg-menu-btn {
    display: inline-flex;
  }
}

@media (min-width: 901px) {
  .mybo-pkg-drawer,
  .mybo-pkg-drawer-backdrop {
    display: none !important;
  }
}
</style>
